import { useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import api from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { toast } from "sonner";

export default function PdfExportButton() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("open");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/items/export/pdf?scope=${scope}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.href = url;
      a.download = `talmia-items-${scope === "open" ? "open-in-progress" : "all"}-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF exported", { description: "Your download should begin shortly." });
      setOpen(false);
    } catch (e) {
      toast.error("Export failed", { description: "Please try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-white/10 text-slate-200 hover:bg-white/5 hover:text-white transition-colors"
        data-testid="pdf-export-button"
      >
        <FileDown className="w-4 h-4 text-orange-400" />
        PDF Export
      </button>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent
          className="bg-[#0F1420] border-white/10 text-slate-100 sm:max-w-md"
          data-testid="pdf-export-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Export items to PDF</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose which items to include. Each item starts on a new page and includes all discussion notes.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup value={scope} onValueChange={setScope} className="space-y-2 py-2">
            <label
              htmlFor="scope-open"
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                scope === "open" ? "border-orange-400 bg-orange-400/5" : "border-white/10 hover:bg-white/5"
              }`}
              data-testid="pdf-scope-open-option"
            >
              <RadioGroupItem value="open" id="scope-open" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">Open & In Progress only</div>
                <div className="text-xs text-slate-400 mt-0.5">Items with status New or In Progress.</div>
              </div>
            </label>
            <label
              htmlFor="scope-all"
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                scope === "all" ? "border-orange-400 bg-orange-400/5" : "border-white/10 hover:bg-white/5"
              }`}
              data-testid="pdf-scope-all-option"
            >
              <RadioGroupItem value="all" id="scope-all" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">All items</div>
                <div className="text-xs text-slate-400 mt-0.5">Every item across all statuses.</div>
              </div>
            </label>
          </RadioGroup>

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 border border-white/10 disabled:opacity-50"
              data-testid="pdf-export-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={busy}
              className="btn-coral inline-flex items-center gap-2 disabled:opacity-70"
              data-testid="pdf-export-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {busy ? "Generating..." : "Export PDF"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
